import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ShieldCheck, Menu, X, Check, AlertTriangle, Ban, Mail, Phone, Globe,
  Share2, Copy, Download, Loader2, ArrowRight, Twitter, Linkedin, Facebook, MessageCircle,
} from "lucide-react";
import { http, useAuth, SITE, logError } from "@/lib";

export const STATUS = {
  valid: { label: "VALID", cls: "valid", Icon: Check },
  risky: { label: "RISKY", cls: "risky", Icon: AlertTriangle },
  invalid: { label: "INVALID", cls: "invalid", Icon: Ban },
};

const TYPE_ICON = { email: Mail, phone: Phone, ip: Globe };

const ANIM_FADE_UP = { initial: { opacity: 0, y: 12 }, animate: { opacity: 1, y: 0 } };
const BAR_INITIAL = { scaleY: 0 };
const BAR_ANIMATE = { scaleY: 1 };

export function Logo({ className = "" }) {
  return (
    <Link to="/" className={`flex items-center gap-2 font-display font-black tracking-tighter text-xl ${className}`} data-testid="logo">
      <span className="w-7 h-7 bg-accent text-accent-foreground flex items-center justify-center"><ShieldCheck size={18} /></span>
      VERIX
    </Link>
  );
}

const NAV = [
  { to: "/features", label: "Features" },
  { to: "/pricing", label: "Pricing" },
  { to: "/compare", label: "Compare" },
  { to: "/use-cases", label: "Use Cases" },
  { to: "/blog", label: "Blog" },
];

export function Nav() {
  const [open, setOpen] = useState(false);
  const { user } = useAuth();
  const loc = useLocation();
  return (
    <header className="sticky top-0 z-50 border-b border-border bg-background/85 backdrop-blur-md">
      <div className="max-w-7xl mx-auto px-5 h-16 flex items-center justify-between">
        <div className="flex items-center gap-10">
          <Logo />
          <nav className="hidden md:flex items-center gap-7 text-sm font-medium">
            {NAV.map((n) => (
              <Link key={n.to} to={n.to} data-testid={`nav-${n.label.toLowerCase().replace(" ", "-")}`}
                className={`link-underline ${loc.pathname === n.to ? "text-foreground" : "text-muted-foreground hover:text-foreground"}`}>
                {n.label}
              </Link>
            ))}
          </nav>
        </div>
        <div className="hidden md:flex items-center gap-3">
          {user ? (
            <Link to="/dashboard" className="btn-primary px-4 py-2 text-sm" data-testid="nav-dashboard">Dashboard</Link>
          ) : (
            <>
              <Link to="/login" className="text-sm font-medium text-muted-foreground hover:text-foreground" data-testid="nav-login">Log in</Link>
              <Link to="/signup" className="btn-primary px-4 py-2 text-sm" data-testid="nav-signup">Start free</Link>
            </>
          )}
        </div>
        <button className="md:hidden" onClick={() => setOpen(!open)} data-testid="nav-mobile-toggle">{open ? <X /> : <Menu />}</button>
      </div>
      {open && (
        <div className="md:hidden border-t border-border bg-background px-5 py-4 flex flex-col gap-4">
          {NAV.map((n) => <Link key={n.to} to={n.to} onClick={() => setOpen(false)} className="text-sm">{n.label}</Link>)}
          {user ? <Link to="/dashboard" onClick={() => setOpen(false)} className="btn-primary px-4 py-2 text-sm text-center">Dashboard</Link>
            : <><Link to="/login" onClick={() => setOpen(false)} className="text-sm">Log in</Link>
              <Link to="/signup" onClick={() => setOpen(false)} className="btn-primary px-4 py-2 text-sm text-center">Start free</Link></>}
        </div>
      )}
    </header>
  );
}

export function Footer() {
  return (
    <footer className="border-t border-border mt-24">
      <div className="max-w-7xl mx-auto px-5 py-16 grid grid-cols-2 md:grid-cols-5 gap-8 text-sm">
        <div className="col-span-2">
          <Logo />
          <p className="text-muted-foreground mt-4 max-w-xs">Real-time email, phone and IP verification. Verify every signup. Block every fraud.</p>
        </div>
        <div>
          <p className="font-display font-bold mb-3">Product</p>
          <ul className="space-y-2 text-muted-foreground">
            <li><Link to="/features" className="hover:text-foreground">Features</Link></li>
            <li><Link to="/pricing" className="hover:text-foreground">Pricing</Link></li>
            <li><Link to="/use-cases" className="hover:text-foreground">Use Cases</Link></li>
          </ul>
        </div>
        <div>
          <p className="font-display font-bold mb-3">Compare</p>
          <ul className="space-y-2 text-muted-foreground">
            <li><Link to="/vs/twilio-lookup" className="hover:text-foreground">vs Twilio Lookup</Link></li>
            <li><Link to="/vs/zerobounce" className="hover:text-foreground">vs ZeroBounce</Link></li>
            <li><Link to="/compare" className="hover:text-foreground">All comparisons</Link></li>
          </ul>
        </div>
        <div>
          <p className="font-display font-bold mb-3">Company</p>
          <ul className="space-y-2 text-muted-foreground">
            <li><Link to="/about" className="hover:text-foreground">About</Link></li>
            <li><Link to="/contact" className="hover:text-foreground">Contact</Link></li>
            <li><Link to="/blog" className="hover:text-foreground">Blog</Link></li>
          </ul>
        </div>
      </div>
      <div className="border-t border-border py-6 text-center text-xs text-muted-foreground">
        © 2026 Verix. Built for teams who refuse to pay for bad data.
      </div>
    </footer>
  );
}

export function RiskGauge({ score, status }) {
  const cls = STATUS[status]?.cls || "valid";
  const seg = 20;
  const filled = Math.round((score / 100) * seg);
  return (
    <div data-testid="risk-score-gauge" className="flex flex-col items-center">
      <div className="flex items-end gap-[3px] h-16">
        {Array.from({ length: seg }).map((_, i) => (
          <motion.div key={`seg-${i}`} initial={BAR_INITIAL} animate={BAR_ANIMATE} transition={{ delay: i * 0.02 }}
            className={`w-2 origin-bottom ${i < filled ? `bg-${cls}` : "bg-secondary"}`}
            style={{ height: `${30 + (i / seg) * 70}%` }} />
        ))}
      </div>
      <div className="mt-3 text-center">
        <div className={`font-display font-black text-5xl text-${cls}`}>{score}</div>
        <div className="text-xs text-muted-foreground uppercase tracking-[0.2em]">Trust Score</div>
      </div>
    </div>
  );
}

export function ResultCard({ result, shareable = true }) {
  const s = STATUS[result.status] || STATUS.valid;
  const { Icon } = s;
  const TypeIcon = TYPE_ICON[result.type] || Globe;
  return (
    <motion.div {...ANIM_FADE_UP}
      data-testid="verification-result-card" className="border border-border bg-card">
      <div className={`flex items-center justify-between px-5 py-3 border-b border-${s.cls}`}>
        <div className="flex items-center gap-2 font-mono text-sm">
          <TypeIcon size={15} />
          <span className="text-muted-foreground uppercase">{result.type}</span>
        </div>
        <div className={`flex items-center gap-2 px-3 py-1 bg-${s.cls} text-background font-display font-bold text-sm`}>
          <Icon size={15} /> {s.label}
        </div>
      </div>
      <div className="p-5 grid sm:grid-cols-[auto_1fr] gap-6 items-center">
        <RiskGauge score={result.score} status={result.status} />
        <div>
          <p className="font-mono text-sm text-muted-foreground break-all mb-3">{result.input}</p>
          <div className="space-y-1.5">
            {result.signals?.map((sig) => (
              <div key={sig.label} className="flex items-center justify-between text-sm border-b border-border/60 pb-1.5">
                <span className="text-muted-foreground">{sig.label}</span>
                <span className={`font-mono text-xs flex items-center gap-1.5 ${sig.ok ? "text-valid" : "text-risky"}`}>
                  {sig.value} {sig.ok ? <Check size={13} /> : <AlertTriangle size={13} />}
                </span>
              </div>
            ))}
          </div>
          {result.ai_summary && (
            <p className="mt-3 text-sm border-l-2 border-accent pl-3 text-foreground/90 italic">{result.ai_summary}</p>
          )}
        </div>
      </div>
      {shareable && result.public_id && <ShareBar result={result} />}
    </motion.div>
  );
}

export function ShareBar({ result }) {
  const [copied, setCopied] = useState(false);
  const url = `${SITE}/r/${result.public_id}`;
  const text = `I just verified this ${result.type} with Verix — ${result.status.toUpperCase()} (trust score ${result.score}/100).`;
  const enc = encodeURIComponent;
  const links = [
    { Icon: Twitter, label: "X", href: `https://twitter.com/intent/tweet?text=${enc(text)}&url=${enc(url)}` },
    { Icon: Linkedin, label: "LinkedIn", href: `https://www.linkedin.com/sharing/share-offsite/?url=${enc(url)}` },
    { Icon: Facebook, label: "Facebook", href: `https://www.facebook.com/sharer/sharer.php?u=${enc(url)}` },
    { Icon: MessageCircle, label: "WhatsApp", href: `https://wa.me/?text=${enc(text + " " + url)}` },
  ];
  const share = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: "Verix result", text, url }); return; }
      catch (e) { if (e?.name !== "AbortError") logError("Web Share failed:", e); }
    }
  };
  const copy = () => { navigator.clipboard.writeText(url); setCopied(true); setTimeout(() => setCopied(false), 1500); };
  return (
    <div className="border-t border-border px-5 py-3 flex flex-wrap items-center gap-2" data-testid="share-bar">
      <span className="text-xs uppercase tracking-widest text-muted-foreground mr-1">Share</span>
      <button onClick={share} className="btn-ghost p-2" data-testid="share-native" title="Share"><Share2 size={15} /></button>
      {links.map((l) => (
        <a key={l.label} href={l.href} target="_blank" rel="noreferrer" className="btn-ghost p-2" title={l.label} data-testid={`share-${l.label.toLowerCase()}`}>
          <l.Icon size={15} />
        </a>
      ))}
      <button onClick={copy} className="btn-ghost p-2 flex items-center gap-1.5 text-xs px-3" data-testid="share-copy">
        <Copy size={14} /> {copied ? "Copied!" : "Copy link"}
      </button>
    </div>
  );
}

const TABS = [
  { key: "email", label: "Email", Icon: Mail, placeholder: "name@company.com", sample: "test@mailinator.com" },
  { key: "phone", label: "Phone", Icon: Phone, placeholder: "+1 415 555 2671", sample: "+14155552671" },
  { key: "ip", label: "IP", Icon: Globe, placeholder: "8.8.8.8", sample: "8.8.8.8" },
];

function WidgetTabs({ tab, onSelect }) {
  return (
    <div className="flex border-b border-border">
      {TABS.map((x) => (
        <button key={x.key} onClick={() => onSelect(x.key)} data-testid={`verify-tab-${x.key}`}
          className={`flex-1 flex items-center justify-center gap-2 py-3 text-sm font-medium transition-all ${tab === x.key ? "bg-accent text-accent-foreground" : "text-muted-foreground hover:bg-secondary"}`}>
          <x.Icon size={15} /> {x.label}
        </button>
      ))}
    </div>
  );
}

export function VerifyWidget({ demo = true, onResult, explain = false }) {
  const [tab, setTab] = useState("email");
  const [value, setValue] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const t = TABS.find((x) => x.key === tab);

  const run = async (val) => {
    const v = (val ?? value).trim();
    if (!v) return;
    setLoading(true); setError(""); setResult(null);
    try {
      const ep = demo ? "/verify/demo" : "/verify";
      const { data } = await http.post(ep, { type: tab, value: v, region: "US", explain });
      setResult(data); onResult?.(data);
    } catch (e) {
      setError(e.response?.data?.detail ? String(e.response.data.detail) : "Verification failed");
    } finally { setLoading(false); }
  };

  return (
    <div className="w-full">
      <div className="border border-border bg-card">
        <WidgetTabs tab={tab} onSelect={(k) => { setTab(k); setResult(null); setValue(""); }} />
        <div className="p-4">
          <div className="flex flex-col sm:flex-row gap-2">
            <input value={value} onChange={(e) => setValue(e.target.value)} onKeyDown={(e) => e.key === "Enter" && run()}
              placeholder={t.placeholder} data-testid="verify-input"
              className="flex-1 bg-background border border-border px-4 py-3 font-mono text-sm outline-none focus:border-accent" />
            <button onClick={() => run()} disabled={loading} data-testid="verify-submit"
              className="btn-accent px-6 py-3 text-sm flex items-center justify-center gap-2 min-w-[120px]">
              {loading ? <Loader2 className="animate-spin" size={16} /> : <>Verify <ArrowRight size={16} /></>}
            </button>
          </div>
          <button onClick={() => { setValue(t.sample); run(t.sample); }} className="mt-2 text-xs text-muted-foreground hover:text-accent" data-testid="verify-sample">
            Try a sample → <span className="font-mono">{t.sample}</span>
          </button>
        </div>
      </div>
      {error && <p className="mt-3 text-sm text-invalid font-mono" data-testid="verify-error">{error}</p>}
      {result && <div className="mt-4"><ResultCard result={result} /></div>}
    </div>
  );
}

export function FaqList({ faqs }) {
  const [open, setOpen] = useState(0);
  return (
    <div className="divide-y divide-border border-y border-border" data-testid="faq-list">
      {faqs.map((f, i) => (
        <div key={f.q} className="py-4">
          <button onClick={() => setOpen(open === i ? -1 : i)} className="w-full flex items-center justify-between text-left font-display font-bold" data-testid={`faq-${i}`}>
            {f.q} <span className="text-accent">{open === i ? "−" : "+"}</span>
          </button>
          {open === i && <p className="mt-3 text-muted-foreground text-sm leading-relaxed">{f.a}</p>}
        </div>
      ))}
    </div>
  );
}

export function Section({ children, className = "" }) {
  return <section className={`max-w-7xl mx-auto px-5 ${className}`}>{children}</section>;
}
